/**
 * Parses the AuditWhiz agent's response — the concatenated `token` text from
 * {@link collectAgentStream} — into the review flow's `ReviewQuestion[]`.
 *
 * The agent returns a JSON document (wrapped in a ```json fence) with shape:
 *   { status, message, suggested_sop, questions[] }
 * where each question carries snake_case fields (`question`, `audit_instruction`,
 * `sub_questions`, `source_policy_id`, …). This module strips the fence, parses,
 * validates `status`, and maps the agent shape onto the Helios-backed
 * `ReviewQuestion` contract the wizard already edits. Pure — no React, no I/O —
 * so it is unit-tested directly against the captured beta samples.
 *
 * See `logic/__tests__/fixtures/agent-samples/` for the real request/response
 * captures this parser is validated against.
 */
import {
  ReviewQuestionStatus,
  type ReviewQuestion,
  type ReviewQuestionConfiguration,
} from './reviewTypes';
import type { SubQuestion } from 'src/types/helios/Question';
import { buildSourceSopDocLinks } from './sourceSopDocLinks';

/** Business unit stamped on a drafted question's configuration. The agent
 *  scopes SOP search by BU but doesn't echo it per question, so the caller's
 *  active BU is threaded in. */
const DEFAULT_BUSINESS_UNIT = 'AMZL';

/** Default audit-score weight for a fresh draft (matches the manual flow seed). */
const DEFAULT_AUDIT_SCORE = 5;

/** The agent's generation outcome. A miss is reported here, NOT via HTTP status
 *  (the stream is a clean 200 even for `no_results`). */
export enum GeneratedQuestionsStatus {
  SUCCESS = 'success',
  NO_RESULTS = 'no_results',
}

/** The parsed, mapped result handed to the review flow. */
export interface GeneratedQuestionsResult {
  readonly status: GeneratedQuestionsStatus;
  /** Human-readable message from the agent, safe to show the user. */
  readonly message: string;
  /** The SOP the agent used — it may have chosen this itself when the caller
   *  sent no `sop_number`, so the UI surfaces it. `null` when none. */
  readonly suggestedSop: string | null;
  /** Mapped draft questions. Empty on `no_results`. */
  readonly questions: readonly ReviewQuestion[];
}

/** Raised when the agent payload can't be parsed or is structurally invalid.
 *  Distinct from the transport errors so the caller can tell "the agent
 *  answered but we couldn't read it" from "the agent was unreachable". */
export class GeneratedQuestionsParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeneratedQuestionsParseError';
  }
}

// ─── Agent wire shapes (snake_case, as received) ─────────────────────────────

interface AgentSubQuestion {
  readonly sub_question_id?: string;
  readonly title?: string;
  readonly sub_question?: string;
}

interface AgentQuestion {
  readonly draft_id?: string;
  readonly source_policy_id?: string;
  readonly title?: string;
  readonly question?: string;
  readonly audit_instruction?: string;
  readonly justification?: string;
  readonly pass_criteria?: string;
  readonly fail_criteria?: string;
  readonly sub_questions?: readonly AgentSubQuestion[];
}

interface AgentResponse {
  readonly status?: string;
  readonly message?: string;
  readonly suggested_sop?: string | null;
  readonly questions?: readonly AgentQuestion[];
}

// ─── Fence stripping ─────────────────────────────────────────────────────────

/** Matches an optional leading ```` ```json ```` (or bare ``` ``` ```) fence and
 *  its closing fence, capturing the JSON body. The agent wraps its document in
 *  a markdown code fence; strip it before `JSON.parse`. */
const JSON_FENCE = /^\s*```(?:json)?\s*([\s\S]*?)\s*```\s*$/;

/** Removes a surrounding markdown code fence if present; otherwise returns the
 *  input trimmed. Tolerant of no fence (so a future un-fenced agent still
 *  parses). */
function stripJsonFence(raw: string): string {
  const match = JSON_FENCE.exec(raw);
  return (match?.[1] ?? raw).trim();
}

// ─── Mapping ─────────────────────────────────────────────────────────────────

/** Pass/Fail requirement toggles for a drafted (sub-)question, all off — the
 *  reviewer sets these in the wizard. */
const REQUIREMENT_TOGGLES_OFF = {
  requireCommentOnFail: false,
  requireEvidenceOnFail: false,
  requireCommentOnPass: false,
  requireEvidenceOnPass: false,
} as const;

/** Default configuration for a freshly generated draft. `sourcePolicyId` is the
 *  per-question `source_policy_id` (the SOP the agent cited); it is seeded as a
 *  locked `isSourceSop` doc link, so even an agent-chosen SOP shows in the
 *  Documentation table and is carried to the published question. */
function buildConfiguration(
  overview: string,
  sourcePolicyId: string,
  businessUnit: string,
): ReviewQuestionConfiguration {
  return {
    overview,
    businessUnit,
    divisionName: '',
    categories: [],
    auditScore: DEFAULT_AUDIT_SCORE,
    tagsIncluded: {},
    locationsExcluded: [],
    ...REQUIREMENT_TOGGLES_OFF,
    docLinks: buildSourceSopDocLinks(sourcePolicyId),
  };
}

/** Maps one agent sub-question onto the Helios `SubQuestion` contract. The
 *  agent's `sub_question` text becomes the instruction; toggles default off. */
function mapSubQuestion(sub: AgentSubQuestion, index: number): SubQuestion {
  return {
    title: sub.title ?? '',
    orderIndex: index + 1,
    instruction: sub.sub_question ?? '',
    ...REQUIREMENT_TOGGLES_OFF,
  };
}

/** Maps one agent question onto a NEEDS_REVIEW `ReviewQuestion`. The `title` is
 *  reused across the panel title, the content title, and the config overview
 *  (same convention as the mock seed). `pass_criteria`/`fail_criteria` are
 *  carried verbatim — they may read "See sub-questions", which the reviewer
 *  resolves against the sub-question list. */
function mapQuestion(
  question: AgentQuestion,
  index: number,
  businessUnit: string,
): ReviewQuestion {
  const title = question.title ?? '';
  const sourcePolicyId = question.source_policy_id ?? '';
  // Same contract as the top-level `questions` array: `sub_questions` may be
  // absent (empty), but a present non-array value (object/string in otherwise-
  // valid JSON) must surface as GeneratedQuestionsParseError, not a raw
  // TypeError from `.map`.
  const rawSubQuestions = question.sub_questions;
  if (rawSubQuestions !== undefined && !Array.isArray(rawSubQuestions)) {
    throw new GeneratedQuestionsParseError(
      `Agent response question ${index + 1} \`sub_questions\` was not an array`,
    );
  }
  // Array.isArray narrows to any[], so re-assert the declared element type.
  const agentSubQuestions: readonly AgentSubQuestion[] = rawSubQuestions ?? [];
  const subQuestions = agentSubQuestions.map(mapSubQuestion);
  return {
    // Stable client-side id for selection/keys — the agent's draft_id when
    // present, else a positional fallback. NOT the published Question ID.
    id: question.draft_id ?? `astra-q-${index + 1}`,
    status: ReviewQuestionStatus.NEEDS_REVIEW,
    title,
    content: {
      questionDetails: {
        title,
        questionText: question.question ?? '',
        auditInstruction: question.audit_instruction ?? '',
        passCriteria: question.pass_criteria ?? '',
        failCriteria: question.fail_criteria ?? '',
        justification: question.justification ?? '',
      },
      subQuestions,
    },
    configuration: buildConfiguration(title, sourcePolicyId, businessUnit),
  };
}

function toStatus(raw: string | undefined): GeneratedQuestionsStatus {
  if (raw === GeneratedQuestionsStatus.SUCCESS) return GeneratedQuestionsStatus.SUCCESS;
  if (raw === GeneratedQuestionsStatus.NO_RESULTS) {
    return GeneratedQuestionsStatus.NO_RESULTS;
  }
  throw new GeneratedQuestionsParseError(
    `Unexpected agent status: ${raw === undefined ? '(missing)' : raw}`,
  );
}

export interface ParseGeneratedQuestionsOptions {
  /** Active business unit, stamped on each drafted question's configuration.
   *  Defaults to AMZL when the caller has none yet. */
  readonly businessUnit?: string;
}

/**
 * Parses the agent's concatenated answer text into a {@link GeneratedQuestionsResult}.
 *
 * @throws {GeneratedQuestionsParseError} when the text isn't valid JSON, isn't an
 *   object, or carries an unrecognized `status`.
 */
export function parseGeneratedQuestions(
  answerText: string,
  options: ParseGeneratedQuestionsOptions = {},
): GeneratedQuestionsResult {
  const businessUnit = options.businessUnit ?? DEFAULT_BUSINESS_UNIT;

  const body = stripJsonFence(answerText);
  if (!body) {
    throw new GeneratedQuestionsParseError('Agent returned an empty response');
  }

  let parsed: AgentResponse;
  try {
    parsed = JSON.parse(body) as AgentResponse;
  } catch {
    throw new GeneratedQuestionsParseError('Agent response was not valid JSON');
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new GeneratedQuestionsParseError('Agent response was not a JSON object');
  }

  const status = toStatus(parsed.status);
  // `questions` may be absent (treated as empty), but if present it MUST be an
  // array — a non-array value (object/string/null in otherwise-valid JSON) would
  // throw a raw TypeError from `.map`, breaking this module's contract that bad
  // agent payloads surface as GeneratedQuestionsParseError (so the caller can
  // tell "answered but unreadable" from "unreachable").
  const rawQuestions = parsed.questions;
  if (rawQuestions !== undefined && !Array.isArray(rawQuestions)) {
    throw new GeneratedQuestionsParseError('Agent response `questions` was not an array');
  }
  // Array.isArray narrows to `any[]`, so re-assert the declared element type for
  // the map (otherwise `question` is `any` and trips no-unsafe-argument).
  const agentQuestions: readonly AgentQuestion[] = rawQuestions ?? [];
  const questions = agentQuestions.map((question, index) =>
    mapQuestion(question, index, businessUnit),
  );

  return {
    status,
    message: parsed.message ?? '',
    suggestedSop: parsed.suggested_sop ?? null,
    questions,
  };
}
